import cx from 'classnames'
import React, { useEffect, MouseEvent, useState, useCallback } from 'react'

import { BLAST_BUTTON_TITLE } from '../../constants/constants'
import { selectLastBlasts } from '../../selectors/current-play-selectors'
import { getModeFlags, selectIsLandscape, selectMode } from '../../selectors/session-selectors'
import trackMixer from '../../services/TrackMixer'
import { isMobile as getIsMobile } from '../../util/track-utils'
import { BlastStatus } from '../../types'
import { useAppSelector } from '../../hooks'

type Props = {
  isRight: boolean
  playerName?: string
}

const BlastButton = ({ isRight, playerName }: Props) => {
  const isMobile = getIsMobile()
  const isLandscape = useAppSelector(selectIsLandscape)
  const { isVizMode } = getModeFlags(useAppSelector(selectMode))
  const { isPlaying } = useAppSelector((state) => state.currentPlay)
  const lastBlasts = useAppSelector(selectLastBlasts)
  const defaultBlastStatus = useCallback(
    (): BlastStatus => ({
      isPressed: false,
      isRight,
      blastRank: 'bad',
    }),
    [isRight]
  )
  const [status, setStatus] = useState<BlastStatus>(defaultBlastStatus())
  useEffect(() => {
    if (lastBlasts.length === 1) {
      const { isRight: isLastBlastRight } = lastBlasts[0]
      const newStatus = isRight === isLastBlastRight ? lastBlasts[0] : defaultBlastStatus()
      setStatus(newStatus)
    } else if (lastBlasts.length === 2) {
      const newStatus = isRight ? lastBlasts[1] : lastBlasts[0]
      setStatus(newStatus)
    }
  }, [lastBlasts, isRight, defaultBlastStatus])

  const onBlastClick = ({ event, isPressed }: { event: MouseEvent; isPressed: boolean }) => {
    if (isPlaying) {
      if (isPressed) {
        trackMixer.defaultGamer?.blast(isRight)
      } else {
        trackMixer.defaultGamer?.releaseBlast(isRight)
      }
    }
    event.preventDefault()
  }
  const onBlastDown = (event: MouseEvent) => {
    onBlastClick({ event, isPressed: true })
  }
  const onBlastUp = (event: MouseEvent) => {
    onBlastClick({ event, isPressed: false })
  }

  const wrapperClassName = cx('blastButtonWrapper', { withBorder: isMobile && !isLandscape })
  const blastButtonClassName = cx('blastButton', {
    hidden: isVizMode,
    blasting: status.isPressed,
    mobile: isMobile,
    right: isRight,
    left: !isRight,
    [`${status.blastRank}1`]: true,
  })
  const playerNameClassName = cx('playerName', { right: isRight })
  return (
    <div className={wrapperClassName}>
      {!isMobile && playerName && <div className={playerNameClassName}>{playerName}</div>}
      <button
        className={blastButtonClassName}
        onMouseDown={onBlastDown}
        onMouseUp={onBlastUp}
        title={BLAST_BUTTON_TITLE}
        tabIndex={-1}
      />
    </div>
  )
}

export default BlastButton
